/**
 * Execution Details Row Component
 *
 * An expandable table row that shows execution details when clicked:
 * - Full error message
 * - Record processing breakdown
 * - Batch and job IDs
 * - Parent/child hierarchy support with tree lines
 */

import React, { useState } from 'react';
import { type TaskExecution } from '../../lib/api';
import { TableRow, TableCell } from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  ChevronRight,
  ChevronDown,
  CheckCircle2,
  XCircle,
  Loader2,
  TrendingUp,
  Copy,
  Check
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';

interface ExecutionDetailsRowProps {
  execution: TaskExecution;
  // Parent/child hierarchy props
  isParent?: boolean;
  isChild?: boolean;
  isLastChild?: boolean;
  childCount?: number;
  isHierarchyExpanded?: boolean;
  onToggleHierarchy?: () => void;
}

export function ExecutionDetailsRow({
  execution,
  isParent = false,
  isChild = false,
  isLastChild = false,
  childCount = 0,
  isHierarchyExpanded = false,
  onToggleHierarchy
}: ExecutionDetailsRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const formatDuration = (ms?: number | null): string => {
    if (ms == null) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatDateTime = (dateString: string): string => {
    const date = new Date(dateString);
    return format(date, 'MMM d, HH:mm:ss');
  };

  const getStatusBadge = (status: TaskExecution['status']) => {
    switch (status) {
      case 'succeeded':
        return (
          <Badge variant="default" className="bg-green-600">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Success
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="destructive">
            <XCircle className="w-3 h-3 mr-1" />
            Failed
          </Badge>
        );
      case 'running':
        return (
          <Badge variant="secondary">
            <Loader2 className="w-3 h-3 mr-1 animate-spin" />
            Running
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const hasDetails = execution.error ||
    execution.recordsFound != null ||
    execution.recordsSuccessful != null ||
    execution.recordsFailed != null ||
    execution.batchId ||
    execution.jobId;

  const formatNumber = (value?: number | null): string => {
    if (value == null) return "N/A";
    return value.toLocaleString();
  };

  return (
    <>
      {/* Main Row */}
      <TableRow
        className={cn(
          "cursor-pointer hover:bg-muted/50",
          isExpanded && "bg-muted/30",
          isChild && "bg-muted/20"
        )}
        onClick={() => {
          // For parent rows with children:
          // - First click: expand hierarchy (show children)
          // - Subsequent clicks when already expanded: toggle details
          // For other rows: clicking toggles details
          if (isParent && childCount > 0) {
            if (!isHierarchyExpanded) {
              // First click - expand to show children
              onToggleHierarchy?.();
            } else if (hasDetails) {
              // Already showing children - toggle details
              setIsExpanded(!isExpanded);
            }
          } else if (hasDetails) {
            setIsExpanded(!isExpanded);
          }
        }}
      >
        <TableCell className="w-8">
          <div className="flex items-center gap-1">
            {/* Tree line for child rows */}
            {isChild && (
              <span
                className="text-muted-foreground font-mono text-sm w-4 ml-2"
                aria-label={isLastChild ? "Last child task" : "Child task"}
              >
                {isLastChild ? '└' : '├'}
              </span>
            )}
            {/* Single expand button with context-aware behavior:
                - Parent rows with children: Primary colored, toggles hierarchy
                - All other rows: Standard colored, toggles details
                - Row click handles the secondary action (details for expanded parents)
            */}
            {(hasDetails || (isParent && childCount > 0)) && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0"
                onClick={(e) => {
                  e.stopPropagation();
                  if (isParent && childCount > 0) {
                    onToggleHierarchy?.();
                  } else {
                    setIsExpanded(!isExpanded);
                  }
                }}
                title={isParent && childCount > 0
                  ? `${isHierarchyExpanded ? 'Collapse' : 'Expand'} child executions (click row for details)`
                  : `${isExpanded ? 'Collapse' : 'Expand'} execution details`}
              >
                {(isParent && childCount > 0 ? isHierarchyExpanded : isExpanded) ? (
                  <ChevronDown className={cn("h-4 w-4", isParent && childCount > 0 && "text-primary")} />
                ) : (
                  <ChevronRight className={cn("h-4 w-4", isParent && childCount > 0 && "text-primary")} />
                )}
              </Button>
            )}
          </div>
        </TableCell>
        <TableCell className="font-mono text-sm">
          {formatDateTime(execution.startTime)}
        </TableCell>
        <TableCell>
          <div className={cn("font-medium", isChild && "pl-4")}>
            {execution.taskName || 'Unknown Task'}
            {isParent && childCount > 0 && (
              <span className="ml-2 text-xs text-muted-foreground">
                ({childCount} step{childCount !== 1 ? 's' : ''})
              </span>
            )}
          </div>
          {execution.entityMappingName && (
            <div className={cn("text-xs text-muted-foreground", isChild && "pl-4")}>
              {execution.entityMappingName}
            </div>
          )}
        </TableCell>
        <TableCell>{getStatusBadge(execution.status)}</TableCell>
        <TableCell className="font-mono text-sm">
          {formatDuration(execution.durationMs)}
        </TableCell>
        <TableCell>
          {execution.recordsFound != null ? (
            <div className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3 text-muted-foreground" />
              {execution.recordsFound.toLocaleString()}
            </div>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
        <TableCell>
          {execution.error ? (
            <span className="text-xs text-red-600 truncate max-w-xs block">
              {execution.error.substring(0, 50)}
              {execution.error.length > 50 ? '...' : ''}
            </span>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </TableCell>
      </TableRow>

      {/* Expanded Details Row */}
      {isExpanded && (
        <TableRow className="bg-muted/10 hover:bg-muted/10">
          <TableCell colSpan={7} className="py-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-8">
              {/* Record Processing Details */}
              {(execution.recordsFound != null ||
                execution.recordsSuccessful != null ||
                execution.recordsFailed != null ||
                execution.recordsSkipped != null ||
                execution.recordsProcessed != null) && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Record Processing</h4>
                  <div className="space-y-1 text-sm">
                    {execution.recordsFound != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Found:</span>
                        <span>{formatNumber(execution.recordsFound)}</span>
                      </div>
                    )}
                    {execution.recordsProcessed != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Processed:</span>
                        <span>{formatNumber(execution.recordsProcessed)}</span>
                      </div>
                    )}
                    {execution.recordsSuccessful != null && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Successful:</span>
                        <span className="text-green-600">{formatNumber(execution.recordsSuccessful)}</span>
                      </div>
                    )}
                    {execution.recordsFailed != null && execution.recordsFailed > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Failed:</span>
                        <span className="text-red-600">{formatNumber(execution.recordsFailed)}</span>
                      </div>
                    )}
                    {execution.recordsSkipped != null && execution.recordsSkipped > 0 && (
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Skipped:</span>
                        <span className="text-yellow-600">{formatNumber(execution.recordsSkipped)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* IDs and Metadata */}
              <div>
                <h4 className="text-sm font-medium mb-2">Execution Details</h4>
                <div className="space-y-2 text-sm">
                  {execution.executionId && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Execution ID:</span>
                      <code className="text-xs bg-muted px-2 py-0.5 rounded">
                        {execution.executionId.substring(0, 16)}...
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(execution.executionId, 'executionId');
                        }}
                      >
                        {copiedField === 'executionId' ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  )}
                  {execution.batchId && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Batch ID:</span>
                      <code className="text-xs bg-muted px-2 py-0.5 rounded">
                        {execution.batchId.substring(0, 16)}...
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(execution.batchId!, 'batchId');
                        }}
                      >
                        {copiedField === 'batchId' ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  )}
                  {execution.jobId && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Job ID:</span>
                      <code className="text-xs bg-muted px-2 py-0.5 rounded">
                        {execution.jobId.substring(0, 16)}...
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-5 w-5 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(execution.jobId!, 'jobId');
                        }}
                      >
                        {copiedField === 'jobId' ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  )}
                  {execution.endTime && (
                    <div>
                      <span className="text-muted-foreground">Completed:</span>{' '}
                      {formatDateTime(execution.endTime)}
                    </div>
                  )}
                </div>
              </div>

              {/* Error Details */}
              {execution.error && (
                <div className="md:col-span-2">
                  <h4 className="text-sm font-medium mb-2 text-red-600">Error Details</h4>
                  <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded p-3">
                    <pre className="text-xs text-red-800 dark:text-red-300 whitespace-pre-wrap break-words">
                      {execution.error}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
